import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import apiServices from "../../../../services/apiService";
import CustomSelect from "../../../common/formfields/CustomSelect";
import { primaryColor, secondaryColor } from "../../../../utils/comon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faSpinner, faTrash, faUser, faBriefcase, faShieldHeart, faCamera, faUpload, faTimes } from "@fortawesome/free-solid-svg-icons";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import DeleteModal from "../../../common/commonmodal/DeleteModal";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";

const UserManagementForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userId = location.state?.userId;
    const isEdit = !!userId;
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [errors, setErrors] = useState({});

    // Webcam states
    const [showWebcam, setShowWebcam] = useState(false);
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    // Get logged-in user id from Redux (token decoded in authSlice)
    const { user: authUser } = useSelector((state) => state.auth);
    const loggedInUserId = authUser?.userId || authUser?.id;
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        mobileNumber: "",
        designation: "",
        dateOfBirth: "",
        dateOfJoining: "",
        department: "",
        age: "",
        gender: "",
        userAddress: "",
        parkingStatus: "",
        employeeEmergencyContact: "",
        visitingCardStatus: "",
        employeeBloodGroup: "",
        roleId: "",
        siteId: "",
        wingId: "",
        floorId: "",
        unitId: "",
        userImage: ""
    });

    const [roleList, setRoleList] = useState([]);
    const [unitList, setUnitList] = useState([]);
    const [loggedInUserRole, setLoggedInUserRole] = useState("");

    const fetchUnits = async () => {
        try {
            const res = await apiServices({ method: "GET", url: "/UnitMaster/GetAllUnitMaster" });
            if (res?.success || res?.IsSuccess) {
                setUnitList(res.data?.data || res.data || []);
            }
        } catch (error) {
            console.error("Error fetching units:", error);
        }
    };

    // ── fetch roles dropdown ──
    const fetchDropdowns = async () => {
        try {
            const res = await apiServices({ method: "GET", url: "/RoleMaster/GetAllRoles" });
            if (res?.success) {
                setRoleList(res.data?.data || []);
            }
        } catch (error) {
            console.error("Error fetching roles:", error);
        }
    };

    const fetchLoggedInUserLocation = async () => {
        if (!loggedInUserId) {
            console.warn("No loggedInUserId found in token");
            return;
        }
        try {
            const res = await apiServices({
                method: 'POST',
                url: '/CommUserDetails/GetCommercialUserById',
                data: { userId: loggedInUserId }
            });
            if (res?.success) {
                const userData = res.data?.Commuserdetailsdata?.[0];
                if (userData) {
                    const roleName = userData.RoleName || userData.roleName || "";
                    setLoggedInUserRole(roleName);

                    setFormData(prev => {
                        const isSiteAdmin = roleName.toLowerCase().includes('site admin') || roleName.toLowerCase().includes('super');
                        return {
                            ...prev,
                            siteId: userData.siteId || prev.siteId,
                            wingId: userData.WingId || prev.wingId,
                            floorId: userData.FloorId || prev.floorId,
                            // If Site Admin and it's a new user creation, force them to select a unit
                            unitId: isSiteAdmin && !isEdit ? "" : (userData.UnitId || prev.unitId)
                        };
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching logged-in user location:", error);
        }
    };

    const fetchUserDetails = async () => {
        if (!userId) return;
        try {
            const res = await apiServices({
                method: "GET",
                url: `/CommUserDetails/getUserDetails?Id=${userId}&PageNumber=1&PageSize=10`
            });

            const isSuccess = res?.success === true || res?.data?.success === true;
            const resData = res?.data?.Commuserdetailsdata || res?.data?.data || res?.data || [];
            const raw = Array.isArray(resData) ? resData[0] : resData;

            if (raw) {
                setFormData(prev => ({
                    ...prev,
                    firstName: raw.FirstName || "",
                    lastName: raw.LastName || "",
                    email: raw.Email || "",
                    mobileNumber: raw.MobileNumber || "",
                    designation: raw.Designation || "",
                    dateOfBirth: raw.DateOfBirth?.split('T')[0] || "",
                    dateOfJoining: raw.DateOfJoining?.split('T')[0] || "",
                    department: raw.DepartmentName || "",
                    age: raw.Age || "",
                    gender: raw.Gender || "",
                    userAddress: raw.UserAddress || "",
                    parkingStatus: raw.ParkingStatus || "",
                    employeeEmergencyContact: raw.EmployeeEmergencyContact || "",
                    visitingCardStatus: raw.VisitingCardStatus || "",
                    employeeBloodGroup: raw.EmployeeBloodGroup || "",
                    roleId: raw.RoleId || "",
                    userImage: raw.UserImage || "",
                    siteId: raw.siteId || "",
                    wingId: raw.WingId || "",
                    floorId: raw.FloorId || "",
                    unitId: raw.UnitId || ""
                }));
            } else {
                console.warn("fetchUserDetails: no user data found in response", res);
            }
        } catch (error) {
            console.error("Error fetching user details:", error);
        }
    };

    // ── useEffect: all functions defined above ──
    useEffect(() => {
        fetchDropdowns();
        fetchUnits();
        fetchLoggedInUserLocation();
        if (isEdit) {
            fetchUserDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [id]: value };

            if (id === "dateOfBirth") {
                if (value) {
                    const birthDate = new Date(value);
                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                        age--;
                    }
                    updated.age = age >= 0 ? age : "";
                } else {
                    updated.age = ""; // clear age if date is cleared
                }
            }

            return updated;
        });
    };

    const handleSelectChange = (id, option) => {
        const val = option?.value || "";
        setFormData(prev => ({ ...prev, [id]: val }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, userImage: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const startWebcam = async () => {
        setShowWebcam(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            streamRef.current = stream;
            // Wait for modal to render and videoRef to attach
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            }, 100);
        } catch (err) {
            console.error("Error accessing webcam:", err);
            toast.error("Could not access webcam. Please check permissions.");
            setShowWebcam(false);
        }
    };

    const stopWebcam = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setShowWebcam(false);
    };

    const capturePhoto = () => {
        if (videoRef.current) {
            const canvas = document.createElement("canvas");
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL("image/jpeg");
            setFormData(prev => ({ ...prev, userImage: dataUrl }));
            stopWebcam();
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.userImage) newErrors.userImage = "Photo is required";
        if (!formData.firstName) newErrors.firstName = "First name is required";
        if (!formData.lastName) newErrors.lastName = "Last name is required";
        if (!formData.email) newErrors.email = "Email is required";
        if (!formData.mobileNumber) newErrors.mobileNumber = "Mobile number is required";
        if (!formData.dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";
        if (formData.age === "" || formData.age === null) newErrors.age = "Age is required";
        if (!formData.gender) newErrors.gender = "Gender is required";
        if (!formData.employeeBloodGroup) newErrors.employeeBloodGroup = "Blood group is required";

        if (!formData.unitId) newErrors.unitId = "Unit is required";
        if (!formData.roleId) newErrors.roleId = "Role is required";
        if (!formData.department) newErrors.department = "Department is required";
        if (!formData.designation) newErrors.designation = "Designation is required";
        if (!formData.dateOfJoining) newErrors.dateOfJoining = "Date of joining is required";

        if (!formData.userAddress) newErrors.userAddress = "Address is required";
        if (!formData.employeeEmergencyContact) newErrors.employeeEmergencyContact = "Emergency contact is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error("Please fill in all mandatory fields.");
            return;
        }

        setIsLoading(true);
        try {
            const url = isEdit ? `/CommUserDetails/putUserDetails/${userId}` : "/CommUserDetails/postUserDetails";
            const method = isEdit ? "PUT" : "POST";

            const payload = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                mobileNumber: formData.mobileNumber?.toString() || "",
                designation: formData.designation,
                dateOfBirth: formData.dateOfBirth || null,
                dateOfJoining: formData.dateOfJoining || null,
                departmentName: formData.department,
                age: formData.age ? Number(formData.age) : null,
                gender: formData.gender,
                userAddress: formData.userAddress,
                parkingStatus: formData.parkingStatus,
                employeeEmergencyContact: formData.employeeEmergencyContact,
                employeeBloodGroup: formData.employeeBloodGroup,
                roleId: formData.roleId,
                siteId: formData.siteId,
                wingId: formData.wingId,
                floorId: formData.floorId,
                unitId: formData.unitId,
                userImage: formData.userImage,
                createdBy: String(loggedInUserId)
            };

            const res = await apiServices({ method, url, data: payload });
            if (res?.success) {
                toast.success(`User ${isEdit ? 'updated' : 'created'} successfully`);
                navigate("/zentcomm/user-management");
            } else {
                toast.error(res.message || "Failed to save user");
            }
        } catch (error) {
            toast.error("Error saving user details");
        }
        setIsLoading(false);
    };

    const confirmDelete = async () => {
        try {
            const res = await apiServices({
                method: "DELETE",
                url: `/CommUserDetails/deleteUserDetails/${userId}`
            });
            if (res?.isSuccess || res?.success || res?.IsSuccess) {
                toast.success("User deleted successfully");
                navigate("/zentcomm/user-management");
            } else {
                toast.error(res.message || "Failed to delete user");
            }
        } catch (error) {
            toast.error("Error deleting user");
        }
    };


    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} User Details`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} user.`}
            />

            <div className="flex flex-col gap-8 mt-6">
                {/* Personal Information Section */}
                <div className="bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={faUser} className="text-sm" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary tracking-tight">Personal Information</h3>
                    </div>

                    <div className="flex flex-col md:flex-row gap-6 mb-6">
                        <div className="flex-shrink-0 flex flex-col items-center justify-start">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-2">Profile Photo <span className="text-red-500">*</span></label>
                            <div className={`relative group ${showWebcam ? 'w-48 h-48 rounded-xl' : 'w-24 h-24 rounded-full'} border-2 border-dashed ${errors.userImage ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-gray-50'} transition-all duration-300 flex items-center justify-center overflow-hidden mb-3`}>
                                {showWebcam ? (
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        className="w-full h-full object-cover transform scale-x-[-1]"
                                    ></video>
                                ) : formData.userImage ? (
                                    <img src={formData.userImage} alt="Profile" className="w-full h-full object-cover" />
                                ) : (
                                    <div className={`flex flex-col items-center justify-center ${errors.userImage ? 'text-red-400' : 'text-gray-400'} transition-colors`}>
                                        <FontAwesomeIcon icon={faUser} className="text-2xl mb-1 opacity-50" />
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-2 w-full justify-center">
                                {!showWebcam && (
                                    <div className="relative">
                                        <button type="button" className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-[10px] font-semibold hover:bg-blue-100 transition-colors flex items-center gap-1">
                                            <FontAwesomeIcon icon={faUpload} /> Upload
                                        </button>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                    </div>
                                )}
                                {showWebcam ? (
                                    <>
                                        <button type="button" onClick={capturePhoto} className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-[10px] font-semibold hover:bg-blue-700 transition-colors flex items-center gap-1">
                                            <FontAwesomeIcon icon={faCamera} /> Snap
                                        </button>
                                        <button type="button" onClick={stopWebcam} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-md text-[10px] font-semibold hover:bg-gray-200 transition-colors flex items-center gap-1">
                                            <FontAwesomeIcon icon={faTimes} /> Cancel
                                        </button>
                                    </>
                                ) : (
                                    <button type="button" onClick={startWebcam} className="px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-md text-[10px] font-semibold hover:bg-emerald-100 transition-colors flex items-center gap-1">
                                        <FontAwesomeIcon icon={faCamera} /> Capture
                                    </button>
                                )}
                            </div>
                            {errors.userImage && <p className="text-red-500 text-[10px] mt-1 text-center">{errors.userImage}</p>}
                        </div>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="firstName">First Name <span className="text-red-500">*</span></label>
                                <input className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.firstName ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} type="text" id="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="Enter first name" />
                                {errors.firstName && <p className="text-red-500 text-[10px] mt-1">{errors.firstName}</p>}
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="lastName">Last Name <span className="text-red-500">*</span></label>
                                <input className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.lastName ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} type="text" id="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Enter last name" />
                                {errors.lastName && <p className="text-red-500 text-[10px] mt-1">{errors.lastName}</p>}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="email">Email <span className="text-red-500">*</span></label>
                            <input className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.email ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} type="email" id="email" value={formData.email} onChange={handleInputChange} placeholder="name@example.com" />
                            {errors.email && <p className="text-red-500 text-[10px] mt-1">{errors.email}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="mobileNumber">Mobile Number <span className="text-red-500">*</span></label>
                            <input className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.mobileNumber ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} type="text" id="mobileNumber" value={formData.mobileNumber} onChange={handleInputChange} maxLength="10" placeholder="10-digit mobile number" />
                            {errors.mobileNumber && <p className="text-red-500 text-[10px] mt-1">{errors.mobileNumber}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="dateOfBirth">Date of Birth <span className="text-red-500">*</span></label>
                            <input className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.dateOfBirth ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} type="date" id="dateOfBirth" value={formData.dateOfBirth} max={new Date().toISOString().split("T")[0]} onChange={handleInputChange} />
                            {errors.dateOfBirth && <p className="text-red-500 text-[10px] mt-1">{errors.dateOfBirth}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="age">Age <span className="text-red-500">*</span></label>
                            <input className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.age ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} type="number" id="age" value={formData.age} onChange={handleInputChange} maxLength="2" placeholder="e.g. 30" />
                            {errors.age && <p className="text-red-500 text-[10px] mt-1">{errors.age}</p>}
                        </div>
                        <div>
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 dark:text-textsecondary mb-1 block">Gender <span className="text-red-500">*</span></span>}
                                options={[{ label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }, { label: 'Other', value: 'other' }]}
                                value={formData.gender ? { label: formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1), value: formData.gender } : null}
                                onChange={(opt) => handleSelectChange("gender", opt)}
                                error={errors.gender}
                                errorMessage={errors.gender}
                            />
                        </div>
                        <div>
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 dark:text-textsecondary mb-1 block">Blood Group <span className="text-red-500">*</span></span>}
                                options={[
                                    { label: 'A+', value: 'A+' },
                                    { label: 'A-', value: 'A-' },
                                    { label: 'B+', value: 'B+' },
                                    { label: 'B-', value: 'B-' },
                                    { label: 'O+', value: 'O+' },
                                    { label: 'O-', value: 'O-' },
                                    { label: 'AB+', value: 'AB+' },
                                    { label: 'AB-', value: 'AB-' }
                                ]}
                                value={formData.employeeBloodGroup ? { label: formData.employeeBloodGroup, value: formData.employeeBloodGroup } : null}
                                onChange={(opt) => handleSelectChange("employeeBloodGroup", opt)}
                                placeholder="Select Blood Group"
                                error={errors.employeeBloodGroup}
                                errorMessage={errors.employeeBloodGroup}
                            />
                        </div>
                    </div>
                </div>

                {/* Employment Details Section */}
                <div className="bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={faBriefcase} className="text-sm" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary tracking-tight">Employment Details</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="col-span-1">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 dark:text-textsecondary mb-1 block">Unit <span className="text-red-500">*</span></span>}
                                options={unitList.map(u => ({ label: `${u.UnitName} (${u.UnitNumber})`, value: u.UnitId }))}
                                value={unitList.find(u => String(u.UnitId) === String(formData.unitId))
                                    ? { label: `${unitList.find(u => String(u.UnitId) === String(formData.unitId)).UnitName} (${unitList.find(u => String(u.UnitId) === String(formData.unitId)).UnitNumber})`, value: formData.unitId }
                                    : null}
                                onChange={(opt) => {
                                    const selectedUnit = unitList.find(u => String(u.UnitId) === String(opt?.value));
                                    if (selectedUnit) {
                                        setFormData(prev => ({
                                            ...prev,
                                            unitId: selectedUnit.UnitId,
                                            siteId: selectedUnit.SiteId || prev.siteId,
                                            wingId: selectedUnit.WingId || prev.wingId,
                                            floorId: selectedUnit.FloorId || prev.floorId
                                        }));
                                    } else {
                                        setFormData(prev => ({ ...prev, unitId: "" }));
                                    }
                                }}
                                placeholder="Select a unit"
                                isDisabled={!(loggedInUserRole?.toLowerCase().includes("site admin") || loggedInUserRole?.toLowerCase().includes("super"))}
                                error={errors.unitId}
                                errorMessage={errors.unitId}
                            />
                        </div>
                        <div className="col-span-1">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 dark:text-textsecondary mb-1 block">Role <span className="text-red-500">*</span></span>}
                                options={roleList.map(r => ({ label: r.RoleName, value: r.RoleId }))}
                                value={roleList.find(r => String(r.RoleId) === String(formData.roleId))
                                    ? { label: roleList.find(r => String(r.RoleId) === String(formData.roleId)).RoleName, value: formData.roleId }
                                    : null}
                                onChange={(opt) => handleSelectChange("roleId", opt)}
                                placeholder="Select a role"
                                error={errors.roleId}
                                errorMessage={errors.roleId}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="department">Department <span className="text-red-500">*</span></label>
                            <input className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all ${errors.department ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} type="text" id="department" value={formData.department} onChange={handleInputChange} placeholder="e.g. Engineering" />
                            {errors.department && <p className="text-red-500 text-[10px] mt-1">{errors.department}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="designation">Designation <span className="text-red-500">*</span></label>
                            <input className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all ${errors.designation ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} type="text" id="designation" value={formData.designation} onChange={handleInputChange} placeholder="e.g. Software Engineer" />
                            {errors.designation && <p className="text-red-500 text-[10px] mt-1">{errors.designation}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="dateOfJoining">Date of Joining <span className="text-red-500">*</span></label>
                            <input className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all ${errors.dateOfJoining ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} type="date" id="dateOfJoining" value={formData.dateOfJoining} onChange={handleInputChange} />
                            {errors.dateOfJoining && <p className="text-red-500 text-[10px] mt-1">{errors.dateOfJoining}</p>}
                        </div>
                    </div>
                </div>

                {/* Access & Emergency Section */}
                <div className="bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor mb-4">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={faShieldHeart} className="text-sm" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary tracking-tight">Access & Emergency Info</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="userAddress">Address <span className="text-red-500">*</span></label>
                            <input className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all ${errors.userAddress ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} type="text" id="userAddress" value={formData.userAddress} onChange={handleInputChange} placeholder="Full residential address" />
                            {errors.userAddress && <p className="text-red-500 text-[10px] mt-1">{errors.userAddress}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="employeeEmergencyContact">Emergency Contact <span className="text-red-500">*</span></label>
                            <input className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all ${errors.employeeEmergencyContact ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} type="text" id="employeeEmergencyContact" value={formData.employeeEmergencyContact} onChange={handleInputChange} placeholder="Contact Name & Number" />
                            {errors.employeeEmergencyContact && <p className="text-red-500 text-[10px] mt-1">{errors.employeeEmergencyContact}</p>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-between mt-auto border-t dark:border-bordercolor pt-4">
                <button
                    type="button"
                    onClick={() => navigate("/zentcomm/user-management")}
                    className="px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textsecondary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <div className="flex gap-3">
                    {isEdit && (
                        <button
                            type="button"
                            onClick={() => setIsDeleteOpen(true)}
                            className="px-4 py-2 border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 rounded-md text-xs font-bold hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                            Delete User
                        </button>
                    )}
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                        style={{ backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "var(--color-primaryaccent)";
                            e.currentTarget.style.color = '#FFFFFF';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                            e.currentTarget.style.color = "var(--color-primaryaccent)";
                        }}
                    >
                        {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update User' : 'Save User')}
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={() => setIsDeleteOpen(false)}
                onConfirm={confirmDelete}
                message="Are you sure you want to delete this user? This action cannot be undone."
            />
        </div>
    );
};

export default UserManagementForm;
